import React, { useEffect, useState } from "react";
import { X, Save, Loader2, Plus, Trash2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { adminServiceGet, adminServiceCreate, adminServiceUpdate } from "../api";

const field = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20";
const labelCls = "block text-xs font-semibold text-[#0D1B2A] mb-1 uppercase tracking-wide";

const VISUALS = ["bbps", "pos", "fastag", "collect", "disburse", "vaultpay", "eduerp", "hrms", "verify", "cms"];

const empty = {
  label: "", group: "Financial Technology", kicker: "", title: "", title_accent: "", title_tail: "",
  desc: "", highlights: [{ title: "", sub: "" }], cta: "Explore Now", chips: [{ title: "", sub: "" }],
  visual: "bbps", published: true, order: 0,
};

const PairList = ({ label, items, onChange, max }) => {
  const set = (i, k, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const add = () => onChange([...items, { title: "", sub: "" }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className={labelCls}>{label}</label>
        {items.length < max && (
          <button type="button" onClick={add} className="flex items-center gap-1 text-xs font-semibold text-[#0EA5E9]"><Plus size={13} /> Add</button>
        )}
      </div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2 items-start">
            <input className={field} placeholder="Title" value={it.title} onChange={(e) => set(i, "title", e.target.value)} data-testid={`service-${label.toLowerCase()}-title-${i}`} />
            <input className={field} placeholder="Subtitle" value={it.sub} onChange={(e) => set(i, "sub", e.target.value)} data-testid={`service-${label.toLowerCase()}-sub-${i}`} />
            <button type="button" onClick={() => remove(i)} className="text-red-500 p-2 rounded-lg hover:bg-red-50 shrink-0"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const ServiceEditor = ({ serviceId, onClose, onSaved }) => {
  const { toast } = useToast();
  const [data, setData] = useState(empty);
  const [loading, setLoading] = useState(!!serviceId);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (serviceId) {
      adminServiceGet(serviceId).then((s) => { setData({ ...empty, ...s }); setLoading(false); }).catch(() => setLoading(false));
    }
  }, [serviceId]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    if (!data.label || !data.title) { toast({ title: "Service name and title are required" }); return; }
    setSaving(true);
    try {
      const payload = {
        ...data,
        highlights: data.highlights.filter((h) => h.title || h.sub),
        chips: data.chips.filter((c) => c.title || c.sub),
      };
      if (serviceId) await adminServiceUpdate(serviceId, payload);
      else await adminServiceCreate(payload);
      toast({ title: "Service saved 🎉 — changes are now live" });
      onSaved();
    } catch {
      toast({ title: "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl my-6 shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <h2 className="text-lg font-bold text-[#0D1B2A]">{serviceId ? "Edit Service" : "New Service"}</h2>
          <div className="flex items-center gap-2">
            <button onClick={save} disabled={saving} data-testid="service-save-button" className="flex items-center gap-1.5 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-70" style={{ background: "linear-gradient(90deg,#06B6D4,#10B981)" }}>
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save & Publish
            </button>
            <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-100"><X size={18} /></button>
          </div>
        </div>

        {loading ? (
          <div className="p-16 text-center text-[#475569]">Loading…</div>
        ) : (
          <div className="p-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className={labelCls}>Service Name (sidebar)</label><input className={field} value={data.label} onChange={(e) => set("label", e.target.value)} placeholder="e.g. Photon Collect" data-testid="service-label-input" /></div>
              <div><label className={labelCls}>Category (sidebar group)</label><input className={field} value={data.group} onChange={(e) => set("group", e.target.value)} placeholder="e.g. Payment Technology" data-testid="service-group-input" /></div>
            </div>
            <div><label className={labelCls}>Kicker (small tag)</label><input className={field} value={data.kicker} onChange={(e) => set("kicker", e.target.value)} placeholder="INTEGRATE • PAY • SCALE" /></div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div><label className={labelCls}>Title</label><input className={field} value={data.title} onChange={(e) => set("title", e.target.value)} placeholder="Bharat Bill" data-testid="service-title-input" /></div>
              <div><label className={labelCls}>Title Accent (orange)</label><input className={field} value={data.title_accent} onChange={(e) => set("title_accent", e.target.value)} placeholder="Payment" /></div>
              <div><label className={labelCls}>Title Tail</label><input className={field} value={data.title_tail} onChange={(e) => set("title_tail", e.target.value)} placeholder="Platform" /></div>
            </div>
            <div><label className={labelCls}>Description</label><textarea className={field} rows={3} value={data.desc} onChange={(e) => set("desc", e.target.value)} /></div>
            <PairList label="Highlights" items={data.highlights} onChange={(v) => set("highlights", v)} max={4} />
            <PairList label="Chips" items={data.chips} onChange={(v) => set("chips", v)} max={4} />
            <div className="grid sm:grid-cols-3 gap-4">
              <div><label className={labelCls}>CTA Button Text</label><input className={field} value={data.cta} onChange={(e) => set("cta", e.target.value)} /></div>
              <div>
                <label className={labelCls}>Visual Style</label>
                <select className={field} value={data.visual} onChange={(e) => set("visual", e.target.value)} data-testid="service-visual-select">
                  {VISUALS.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div><label className={labelCls}>Order</label><input className={field} type="number" value={data.order} onChange={(e) => set("order", parseInt(e.target.value) || 0)} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm font-semibold text-[#0D1B2A]">
              <input type="checkbox" className="w-4 h-4 accent-[#06B6D4]" checked={data.published} onChange={(e) => set("published", e.target.checked)} />
              Published (visible on the website)
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceEditor;
